import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getSubscriptionStatus, SubscriptionType, isProPlan, isEnterprisePlan } from '../services/subscriptionService';

export interface ModeOption {
    value: string;
    label: string;
    disabled: boolean;
    requiresPlan?: SubscriptionType[];
    description?: string;
}

export interface ModeRestrictions {
    availableModes: ModeOption[];
    isLoading: boolean;
    error: string | null;
    userPlan: string;
    hasInitialLoad: boolean;
    canUseMode: (mode: string) => boolean;
    getRestrictedModeMessage: (mode: string) => string;
}

export const useModeRestrictions = (isImageEditingPage: boolean = false): ModeRestrictions => {
    const { currentUser } = useAuth();
    const [userPlan, setUserPlan] = useState<string>('free');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasInitialLoad, setHasInitialLoad] = useState(false);

    // Define mode restrictions based on plans
    const getModeOptions = (planType: string): ModeOption[] => {
        const plan = planType.toLowerCase();

        return [
            {
                value: 'performance',
                label: 'Performance',
                disabled: false, // Available to all plans
                description: 'Fast generation with good quality'
            },
            {
                value: 'balanced',
                label: isImageEditingPage ? 'Balanced (Nano Banana)' : 'Balanced',
                disabled: false, // Available to all plans
                description: 'Balanced speed and quality'
            },
            {
                value: 'quality',
                label: isImageEditingPage ? 'Quality (Nano Banana Pro)' : 'Quality',
                disabled: !isProPlan(planType) && !isEnterprisePlan(planType),
                requiresPlan: [SubscriptionType.PRO, SubscriptionType.ENTERPRISE],
                description: 'Best quality, slower generation'
            }
        ];
    };

    // Fetch user subscription status
    useEffect(() => {
        const fetchUserPlan = async () => {
            if (!currentUser?.uid) {
                setUserPlan('free');
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);
                const response = await getSubscriptionStatus(currentUser.uid);
                setUserPlan(response.type.toLowerCase());
            } catch (error) {
                console.error('Failed to fetch subscription status:', error);
                setError('Failed to load subscription details');
                setUserPlan('free'); // Default to free on error
            } finally {
                setIsLoading(false);
                setHasInitialLoad(true);
            }
        };

        fetchUserPlan();
    }, [currentUser?.uid]);

    // Helper function to check if a mode is available
    const canUseMode = (mode: string): boolean => {
        const modeOptions = getModeOptions(userPlan);
        const modeOption = modeOptions.find(option => option.value === mode);
        return modeOption ? !modeOption.disabled : false;
    };

    // Helper function to get restriction message
    const getRestrictedModeMessage = (mode: string): string => {
        const modeOptions = getModeOptions(userPlan);
        const modeOption = modeOptions.find(option => option.value === mode);

        if (!modeOption || !modeOption.disabled) {
            return '';
        }

        if (modeOption.requiresPlan) {
            // Get the minimum required plan (the first in the list)
            const minRequiredPlan = modeOption.requiresPlan[0];

            switch (minRequiredPlan) {
                case SubscriptionType.PLUS:
                    return `${modeOption.label} mode requires Plus plan or higher`;
                case SubscriptionType.PRO:
                    return `${modeOption.label} mode requires Pro plan or higher`;
                case SubscriptionType.ENTERPRISE:
                    return `${modeOption.label} mode requires Enterprise plan`;
                default:
                    return `${modeOption.label} mode requires plan upgrade`;
            }
        }

        return `${modeOption.label} mode requires plan upgrade`;
    };

    return {
        availableModes: getModeOptions(userPlan),
        isLoading,
        error,
        userPlan,
        hasInitialLoad,
        canUseMode,
        getRestrictedModeMessage
    };
}; 